import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import {
  convertQuestionMappings,
  convertTriggers,
  convertVisibleIf,
  q,
  v,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  // 需要登入嗎
  const requiresLogin = {
    login: false,
    title: '紙上的編號',
    placeholder: 'T000',
    alert: '編號有誤，請重新輸入，或請工作人員協助',
    // useUserIdLogin: true,
  }
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  const canShowPrevButtonPages = null
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  const userIdRef = useRef(null)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 第4題（複選）情境：1宗教祭祀 2送禮 3追思喪葬 4個人自用 5我都沒有購買過花（互斥 → "none"）
  // 註：q8/13/18/23 的「其他，請說明」o_type 為 0（非 6），故用數字索引而非 "other"，
  //     JSON 補上「沒有第二/第三重要考量」後為第 11 項（共 11 個選項）；
  //     q25/27/33/35/38/40/43/45/48/50 同理，補上「沒有第二/第三…」後其他為第 14 項（共 14 個選項）。
  const visibleIfConditionsInit = convertVisibleIf({
    // 情境1 宗教祭祀 → 第5~9題
    q5: '{q4} anyof [1]',
    q6: '{q4} anyof [1]',
    q7: '{q4} anyof [1]',
    q8: '{q4} anyof [1]',
    q9: '({q4} anyof [1]) and ({q8} anyof [11])',
    // 情境2 送禮 → 第10~14題
    q10: '{q4} anyof [2]',
    q11: '{q4} anyof [2]',
    q12: '{q4} anyof [2]',
    q13: '{q4} anyof [2]',
    q14: '({q4} anyof [2]) and ({q13} anyof [11])',
    // 情境3 追思或喪葬致意 → 第15~19題
    q15: '{q4} anyof [3]',
    q16: '{q4} anyof [3]',
    q17: '{q4} anyof [3]',
    q18: '{q4} anyof [3]',
    q19: '({q4} anyof [3]) and ({q18} anyof [11])',
    // 情境4 個人自用 → 第20~24題
    q20: '{q4} anyof [4]',
    q21: '{q4} anyof [4]',
    q22: '{q4} anyof [4]',
    q23: '{q4} anyof [4]',
    q24: '({q4} anyof [4]) and ({q23} anyof [11])',
    // 第25~29題：第4題有勾任一實際購買情境(1)~(4)才顯示（勾「我都沒有購買過花」者跳過）
    q25: '{q4} anyof [1, 2, 3, 4]',
    q26: '({q4} anyof [1, 2, 3, 4]) and ({q25} anyof [14])',
    q27: '{q4} anyof [1, 2, 3, 4]',
    q28: '({q4} anyof [1, 2, 3, 4]) and ({q27} anyof [14])',
    q29: '{q4} anyof [1, 2, 3, 4]',
    // 第4題勾「我都沒有購買過花」→ 第30~31題
    q30: '{q4} anyof ["none"]',
    q31: '{q4} anyof ["none"]',
    // 花費增減：第32/37/42/47題所有人都問（不掛第4題情境條件），子題只依各自母題的答案。
    // 母題選(2)和去年差不多 或 (4)從來沒有因該情境購買過 → 該段子題全不顯示，
    // 該頁只剩母題，按下一頁即進入下一段母題（= PM 說的「跳至第37/42/47/52題」），無需額外 trigger。
    // 情境1 宗教祭祀：第32題選(1)比去年多 → 33~34、選(3)比去年少 → 35~36
    q33: '{q32} = 1',
    q34: '({q32} = 1) and ({q33} anyof [14])',
    q35: '{q32} = 3',
    q36: '({q32} = 3) and ({q35} anyof [14])',
    // 情境2 送禮
    q38: '{q37} = 1',
    q39: '({q37} = 1) and ({q38} anyof [14])',
    q40: '{q37} = 3',
    q41: '({q37} = 3) and ({q40} anyof [14])',
    // 情境3 追思或喪葬致意
    q43: '{q42} = 1',
    q44: '({q42} = 1) and ({q43} anyof [14])',
    q45: '{q42} = 3',
    q46: '({q42} = 3) and ({q45} anyof [14])',
    // 情境4 個人自用
    q48: '{q47} = 1',
    q49: '({q47} = 1) and ({q48} anyof [14])',
    q50: '{q47} = 3',
    q51: '({q47} = 3) and ({q50} anyof [14])',
    // 第54題未選(4)完全以花卉取代焚香/(5)信仰不需焚香/(6)沒有宗教信仰 → 才顯示第55~56題
    q55: '({q54} notempty) and ({q54} <> 4) and ({q54} <> 5) and ({q54} <> 6)',
    q56: '({q54} notempty) and ({q54} <> 4) and ({q54} <> 5) and ({q54} <> 6)',
    // 第59題選(1)我要參加抽獎 → 才顯示第17頁的姓名/手機；選(2)不用了，謝謝則整頁跳過
    q60: '{q59} = 1',
    q61: '{q59} = 1',
  })
  const notRequired = null
  const mappedRateChoice = null
  const pageBreakQuestions = null
  // 篩出（結束問卷）：資格不符／樣本額滿 →
  // 第1題性別(3)其他、
  // 第2題年齡(1)19歲以下、(14)80歲以上、
  // 第3題地區(1)基隆市、(2)台北市、(3)新北市、(4)桃園市、(5)宜蘭縣、(6)新竹縣、(7)新竹市、
  //          (8)苗栗縣、(9)台中市、(10)彰化縣、(11)南投縣、(12)雲林縣、(13)嘉義縣、(14)嘉義市、
  //          (15)台南市、(16)高雄市、(17)屏東縣、(20)以上皆非
  //          → 僅保留(18)台東縣、(19)花蓮縣兩區。
  // q1~q3 同在第1頁，若不等三題都作答就中止，會漏收同頁其他兩題。
  // 註：第59題不參加抽獎不走 trigger，改用 q60/q61 的 visibleIf 跳過第17頁。
  const q1ScreenOutOptions = [3]
  const q2ScreenOutOptions = [1, 14]
  const q3ScreenOutOptions = [
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 20,
  ]
  const orExpression = (qNo, options) =>
    options.map((n) => `({q${qNo}} = ${n})`).join(' or ')
  const triggersConditionsInit = convertTriggers([
    {
      type: 'complete',
      expression: `(${orExpression(1, q1ScreenOutOptions)} or ${orExpression(
        2,
        q2ScreenOutOptions
      )} or ${orExpression(
        3,
        q3ScreenOutOptions
      )}) and ({q1} notempty) and ({q2} notempty) and ({q3} notempty)`,
    },
  ])
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    requiresLogin,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    requiresLogin,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    webAuthType,
  })

  useEffect(() => {
    if (agreeSurvey) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [agreeSurvey])

  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {}

  // 篩出（不符資格）文案
  const endMessage = `目前與您相同特性的樣本額滿，已停止收卷，可惜沒辦法繼續參與本次活動。如果覺得這個主題很有意思，非常歡迎您分享本問卷連結給親友，或是加入我們的<a href='https://lin.ee/HWETcqy'>LINE@</a>，隨時得到最新填問卷獲好禮的消息。<p>請按「完成」前往下載 EZChoice 或填寫更多問卷</p>`

  const endSurveyMsg = (survey) => {
    const answered = (n) => {
      const val = survey.getValue(q(n))
      return val !== undefined && val !== null && val !== ''
    }

    // 與 triggersConditionsInit 1:1 對應（資格篩出）
    // 第59題不參加抽獎不算篩出，走正常流程到完成頁，仍給正常感謝語
    const isScreenedOut =
      (q1ScreenOutOptions.map(v).includes(survey.getValue(q(1))) ||
        q2ScreenOutOptions.map(v).includes(survey.getValue(q(2))) ||
        q3ScreenOutOptions.map(v).includes(survey.getValue(q(3)))) &&
      answered(1) &&
      answered(2) &&
      answered(3)

    if (isScreenedOut) {
      setCompletionMessage(endMessage)
      return
    }

    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezcapp.page.link/tAQv" target="_blank">https://ezcapp.page.link/tAQv</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    requiresLogin,
    userIdRef,
    canShowPrevButtonPages,
    enableProgressSaving: requiresLogin.login || webAuthType > 0,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
